import React, { useState, useEffect, useRef } from 'react';
import { getRollupSummary, getProject } from '../api';

/* ──────────────────────────────────────────────────────────────────
 * RollupTab — read-only "how much of each SOV line have my tasks
 * earned?" view that sits between the Schedule (where progress is
 * tracked) and the Billing tab (where invoices get cut).
 *
 * Warm-cream + sage token redesign from the Claude Design handoff
 * (`~/Downloads/design_handoff_rollup`). All data continues to flow
 * through the existing `getRollupSummary(:projectId)` API — no
 * backend changes. Auto-computed server-side; no manual recalc.
 *
 * Behaviors preserved from the prior slate-blue version:
 *  - 4 summary tiles + line table + totals footer
 *  - Hover popover on the Tasks Linked count showing each linked
 *    task name + per-task progress bar
 *  - Empty-state when no SOV lines exist
 *
 * Patterns introduced from the redesign:
 *  - Sticky table head + sticky totals footer (BillingG703 pattern)
 *  - Tone-shifting tiles + cells: success at full coverage,
 *    warning while partial, text-4 muting when zero
 *  - 22%-mix soft rings on every chip + tile that uses a tonal fill
 *  - Mono + tabular-nums for every money / percent / count value
 *  - 5-state skeleton loading
 * ────────────────────────────────────────────────────────────────── */

const $f = (v) => {
  const n = parseFloat(v) || 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const num = (v) => parseFloat(v) || 0;

/* ─────────────────────── Inline-SVG icons ─────────────────────── */

const CheckIcon = ({ size = 11, sw = 2.4 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const DocIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="13" y2="17" />
  </svg>
);

/* ─────────────────────── Grid columns ─────────────────────── */

// `grid-template-columns` for both the body rows and the sticky head + footer.
// SOV Line stretches; everything else is fixed so the columns align vertically
// across head/body/footer without subpixel drift.
const GRID = 'minmax(280px, 2.4fr) 150px 110px 160px 230px 150px';

/* ─────────────────────── Summary tile ─────────────────────── */

const Tile = ({ label, value, sub, kind = 'neutral', subIcon }) => {
  const muted = !value || num(value) === 0;
  const palette = {
    neutral: {
      bg: 'var(--bg-surface)',
      border: 'var(--border-subtle)',
      eyebrow: 'var(--text-3)',
      value: muted ? 'var(--text-4)' : 'var(--text-1)',
      sub: 'var(--text-3)',
    },
    success: {
      bg: 'var(--success-soft)',
      border: 'color-mix(in oklch, var(--success) 22%, transparent)',
      eyebrow: 'var(--success)',
      value: muted ? 'var(--text-4)' : 'var(--success)',
      sub: 'var(--success)',
    },
    warning: {
      bg: 'var(--warning-soft)',
      border: 'color-mix(in oklch, var(--warning) 22%, transparent)',
      eyebrow: 'var(--warning)',
      value: muted ? 'var(--text-4)' : 'var(--warning)',
      sub: 'var(--warning)',
    },
  }[kind];

  return (
    <div style={{
      flex: 1, minWidth: 160,
      padding: '14px 18px 16px',
      background: palette.bg,
      border: `1px solid ${palette.border}`,
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 600,
        color: palette.eyebrow,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}>{label}</div>
      <div className="mono" style={{
        fontSize: 24.5, fontWeight: 600,
        color: palette.value,
        letterSpacing: '-0.025em', lineHeight: 1.1,
      }}>
        {typeof value === 'number' ? $f(value) : value}
      </div>
      {sub && (
        <div style={{
          fontSize: 13, color: palette.sub,
          letterSpacing: '-0.005em',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          {subIcon && (
            <span style={{
              width: 13, height: 13, borderRadius: 99,
              background: 'var(--success-soft)',
              color: 'var(--success)',
              border: '1px solid color-mix(in oklch, var(--success) 22%, transparent)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <CheckIcon size={8} sw={2.6} />
            </span>
          )}
          {sub}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────── Skeleton primitives ─────────────────────── */

const SkelBar = ({ width, height = 12 }) => (
  <span style={{
    display: 'inline-block', width, height,
    borderRadius: 4,
    background: 'var(--bg-surface-2)',
    animation: 'rollupPulse 1.4s ease-in-out infinite',
  }} />
);

const TileSkeleton = () => (
  <div style={{
    flex: 1, minWidth: 160,
    padding: '14px 18px 16px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 12,
    boxShadow: 'var(--shadow-sm)',
    display: 'flex', flexDirection: 'column', gap: 8,
  }}>
    <SkelBar width={90} height={11} />
    <SkelBar width={120} height={20} />
    <SkelBar width={140} height={11} />
  </div>
);

/* ─────────────────────── Tasks-linked popover ─────────────────────── */

const LinkedTasksPopover = ({ tasks, lineCount }) => (
  <div style={{
    position: 'absolute',
    top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
    width: 320,
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-default)',
    borderRadius: 8,
    boxShadow: 'var(--shadow-md), 0 16px 32px -8px oklch(0.2 0.01 100 / 0.18)',
    padding: '10px 12px 12px',
    zIndex: 10,
    textAlign: 'left',
  }}>
    {/* 8px rotated tail at the top to point at the chip */}
    <div style={{
      position: 'absolute', top: -5, left: '50%',
      width: 8, height: 8,
      background: 'var(--bg-surface)',
      borderTop: '1px solid var(--border-default)',
      borderLeft: '1px solid var(--border-default)',
      transform: 'translateX(-50%) rotate(45deg)',
    }} />
    <div style={{
      fontSize: 12, fontWeight: 600,
      color: 'var(--text-3)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      marginBottom: 8,
      display: 'flex', alignItems: 'center', gap: 5,
    }}>
      Linked tasks
      <span className="mono" style={{ color: 'var(--text-4)', letterSpacing: '-0.02em' }}>
        ({lineCount})
      </span>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {tasks.map((t, i) => {
        const pct = num(t.percent_complete || t.pct);
        const fillColor = pct >= 99.9 ? 'var(--success)' : pct > 0 ? 'var(--accent)' : 'transparent';
        const pctColor = pct === 0 ? 'var(--text-4)' : pct >= 99.9 ? 'var(--success)' : 'var(--text-2)';
        return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 70px 38px',
            alignItems: 'center', gap: 10,
          }}>
            <div style={{
              fontSize: 14, color: 'var(--text-1)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{t.task_name || t.name}</div>
            <div style={{
              height: 5, borderRadius: 99,
              background: 'var(--border-subtle)', overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%',
                background: fillColor, borderRadius: 99,
              }} />
            </div>
            <div className="mono" style={{
              fontSize: 12.5, color: pctColor,
              letterSpacing: '-0.02em', textAlign: 'right',
            }}>{pct.toFixed(0)}%</div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────── Main ─────────────────────── */

const RollupTab = ({ projectId }) => {
  const [rollupData, setRollupData] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popoverRowId, setPopoverRowId] = useState(null);
  const closeTimer = useRef(null);

  // Inject the skeleton pulse keyframe once. Honors prefers-reduced-motion.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const id = 'rollup-pulse-anim';
    if (document.getElementById(id)) return undefined;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @keyframes rollupPulse {
        0%, 100% { opacity: 0.5; }
        50%      { opacity: 0.9; }
      }
      @media (prefers-reduced-motion: reduce) {
        @keyframes rollupPulse { 0%,100%,50% { opacity: 0.7; } }
      }
    `;
    document.head.appendChild(s);
    return () => {};
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [data, proj] = await Promise.all([
          getRollupSummary(projectId),
          getProject(projectId),
        ]);
        if (cancelled) return;
        setRollupData(data);
        setProject(proj);
      } catch (err) {
        console.error('Error fetching rollup data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [projectId]);

  // Close popover on Esc / outside-click. The chip's own onMouseEnter +
  // onMouseLeave handle hover dismissal; this catches the keyboard +
  // off-popover-click cases.
  useEffect(() => {
    if (popoverRowId == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPopoverRowId(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [popoverRowId]);

  /* ───── Render: Loading skeleton ───── */
  if (loading && !rollupData) {
    return (
      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          {[0, 1, 2, 3].map((i) => <TileSkeleton key={i} />)}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 2px' }}>
          <SkelBar width={220} height={15} />
          <SkelBar width={420} height={11} />
        </div>
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: GRID,
            height: 38, alignItems: 'center',
            padding: '0 12px',
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            {['SOV Line', 'Contract', 'Tasks Linked', 'Allocated', 'Calc % Complete', 'Calc Billed'].map((h) => (
              <div key={h} style={{
                padding: '0 8px',
                fontSize: 12, fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{h}</div>
            ))}
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: GRID,
              minHeight: 56, alignItems: 'center',
              padding: '0 12px',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <SkelBar width={180} height={13} />
                <SkelBar width={110} height={10} />
              </div>
              <div style={{ padding: '0 8px', textAlign: 'right' }}><SkelBar width={70} height={13} /></div>
              <div style={{ padding: '0 8px', textAlign: 'center' }}><SkelBar width={30} height={20} /></div>
              <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <SkelBar width={80} height={13} /><SkelBar width={40} height={9} />
              </div>
              <div style={{ padding: '0 8px' }}><SkelBar width={180} height={6} /></div>
              <div style={{ padding: '0 8px', textAlign: 'right' }}><SkelBar width={70} height={13} /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!rollupData) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-3)' }}>
        No rollup data available.
      </div>
    );
  }

  const { summary, lines } = rollupData;
  const projectContract = num(project?.contract_amount) || num(summary.total_contract);
  const totalContract = num(summary.total_contract);
  const totalAllocated = num(summary.total_allocated);
  const totalBilled = num(summary.total_calc_billed);
  const unallocated = num(summary.unallocated);

  const sovPctOfProject = projectContract > 0 ? (totalContract / projectContract) * 100 : 0;
  const allocPctOfSov = totalContract > 0 ? (totalAllocated / totalContract) * 100 : 0;
  const sovEqualsProject = projectContract > 0 && Math.abs(projectContract - totalContract) < 0.01;
  const fullyAllocated = totalContract > 0 && unallocated <= 0.01;

  // Weighted-average % complete = Σ(allocated × pct) / Σ(allocated)
  const weightedPct = (() => {
    if (totalAllocated <= 0) return 0;
    const w = lines.reduce(
      (s, l) => s + num(l.total_allocated) * num(l.calc_percent_complete),
      0,
    );
    return w / totalAllocated;
  })();
  const totalTasksLinked = lines.reduce((s, l) => s + (l.tasks_linked || 0), 0);

  /* ───── Render: Empty state — no SOV lines ───── */
  if (lines.length === 0) {
    return (
      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Tile label="Project contract" value={projectContract} />
          <Tile label="Total SOV contract" value={0} />
          <Tile label="Total task-allocated" value={0} />
          <Tile label="Unallocated SOV" value={0} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 2px' }}>
          <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>
            SOV Rollup from Tasks
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-3)', letterSpacing: '-0.005em' }}>
            Rollup updates automatically when task progress or SOV allocations change.
          </p>
        </div>
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 12,
          boxShadow: 'var(--shadow-sm)',
          padding: '48px 24px',
          textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'var(--accent-soft)',
            color: 'var(--accent-text)',
            border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <DocIcon />
          </div>
          <div style={{ fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)' }}>
            No SOV lines yet
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 360, lineHeight: 1.5 }}>
            Add lines in the SOV tab first — once tasks are linked, the rollup will populate here automatically.
          </div>
        </div>
      </div>
    );
  }

  /* ───── Render: Main view ───── */

  // Tile 3 (Total Task-Allocated) tone-shifts:
  //   ≥99.9% → success;  0–99.9% → warning;  $0 → neutral muted
  const tile3Kind = allocPctOfSov >= 99.9 ? 'success'
                  : totalAllocated > 0    ? 'warning'
                                          : 'neutral';

  return (
    <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Summary tiles ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <Tile label="Project contract" value={projectContract} />
        <Tile
          label="Total SOV contract"
          value={totalContract}
          sub={`${sovPctOfProject.toFixed(1)}% of project contract`}
          subIcon={sovEqualsProject}
        />
        <Tile
          label="Total task-allocated"
          value={totalAllocated}
          sub={`${allocPctOfSov.toFixed(1)}% of SOV allocated`}
          kind={tile3Kind}
        />
        <Tile
          label="Unallocated SOV"
          value={unallocated}
          sub={fullyAllocated ? 'Fully allocated' : 'remaining to link to tasks'}
          subIcon={fullyAllocated}
          kind={fullyAllocated ? 'success' : 'neutral'}
        />
      </div>

      {/* ── Section title ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 2px' }}>
        <h3 style={{ margin: 0, fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>
          SOV Rollup from Tasks
        </h3>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-3)', letterSpacing: '-0.005em' }}>
          Rollup updates automatically when task progress or SOV allocations change.
        </p>
      </div>

      {/* ── Rollup table ── */}
      <div style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{ maxHeight: 'calc(100vh - 380px)', minHeight: 240, overflow: 'auto', position: 'relative' }}>
          {/* Sticky header */}
          <div style={{
            display: 'grid', gridTemplateColumns: GRID,
            height: 38, alignItems: 'center',
            position: 'sticky', top: 0, zIndex: 3,
            background: 'var(--bg-surface-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            <Th align="left">SOV Line</Th>
            <Th align="right">Contract</Th>
            <Th align="center">Tasks Linked</Th>
            <Th align="right">Allocated</Th>
            <Th align="left">Calc % Complete</Th>
            <Th align="right" noRight>Calc Billed</Th>
          </div>

          {/* Body rows */}
          {lines.map((line, idx) => {
            const contract = num(line.contract_amount);
            const allocated = num(line.total_allocated);
            const allocPct = num(line.allocated_pct);
            const pct = num(line.calc_percent_complete);
            const billed = num(line.calc_billed);
            const tasksLinked = line.tasks_linked || 0;
            const tasks = line.tasks || [];

            const allocTone = allocated === 0          ? 'var(--text-4)'
                           : allocPct >= 99.9          ? 'var(--success)'
                                                       : 'var(--warning)';
            const pctFill = pct >= 99.9 ? 'var(--success)' : pct > 0 ? 'var(--accent)' : 'transparent';
            const pctReadout = pct === 0 ? 'var(--text-4)' : pct >= 99.9 ? 'var(--success)' : 'var(--text-2)';
            const isPopopen = popoverRowId === line.id;
            const isLast = idx === lines.length - 1;

            return (
              <div
                key={line.id}
                style={{
                  display: 'grid', gridTemplateColumns: GRID,
                  minHeight: 56, alignItems: 'center',
                  borderBottom: !isLast ? '1px solid var(--border-subtle)' : 'none',
                  background: 'var(--bg-surface)',
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
              >
                {/* 1 — SOV Line */}
                <div style={{ padding: '8px 12px', minWidth: 0 }}>
                  <div style={{
                    fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{line.description || '–'}</div>
                  <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2, letterSpacing: '-0.02em' }}>
                    {line.sov_code || ''}
                    {line.building_area_phase && (
                      <span style={{ color: 'var(--text-4)' }}>{' · '}{line.building_area_phase}</span>
                    )}
                  </div>
                </div>
                {/* 2 — Contract */}
                <div className="mono" style={{
                  padding: '0 12px', textAlign: 'right',
                  fontSize: 14.5, fontWeight: 500,
                  color: contract === 0 ? 'var(--text-4)' : 'var(--text-1)',
                  letterSpacing: '-0.02em',
                }}>{$f(contract)}</div>
                {/* 3 — Tasks Linked chip + popover */}
                <div style={{
                  padding: '0 12px', textAlign: 'center', position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {tasksLinked > 0 ? (
                    <div
                      onMouseEnter={() => {
                        if (closeTimer.current) clearTimeout(closeTimer.current);
                        setPopoverRowId(line.id);
                      }}
                      onMouseLeave={() => {
                        // Tiny delay so users can move from chip → popover without flicker
                        closeTimer.current = setTimeout(() => setPopoverRowId(null), 80);
                      }}
                      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                    >
                      <span
                        className="mono"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 26, height: 22, padding: '0 8px',
                          borderRadius: 999,
                          fontSize: 13, fontWeight: 600,
                          letterSpacing: '-0.01em',
                          color: isPopopen ? '#fff' : 'var(--accent-text)',
                          background: isPopopen ? 'var(--accent)' : 'var(--accent-soft)',
                          border: `1px solid ${isPopopen ? 'var(--accent)' : 'color-mix(in oklch, var(--accent) 22%, transparent)'}`,
                          boxShadow: isPopopen ? '0 0 0 3px var(--accent-ring)' : 'none',
                          cursor: 'pointer',
                          transition: 'background .12s, color .12s, border-color .12s, box-shadow .12s',
                        }}
                      >{tasksLinked}</span>
                      {isPopopen && tasks.length > 0 && (
                        <LinkedTasksPopover tasks={tasks} lineCount={tasks.length} />
                      )}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-4)', fontSize: 15.5 }}>—</span>
                  )}
                </div>
                {/* 4 — Allocated */}
                <div style={{ padding: '0 12px', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <div className="mono" style={{
                    fontSize: 14.5, fontWeight: 600, color: allocTone,
                    letterSpacing: '-0.02em',
                  }}>{$f(allocated)}</div>
                  <div className="mono" style={{
                    fontSize: 12, color: allocTone, opacity: 0.85,
                    letterSpacing: '-0.02em',
                  }}>{allocPct.toFixed(1)}%</div>
                </div>
                {/* 5 — Calc % Complete */}
                <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    flex: 1, height: 6, borderRadius: 99,
                    background: 'var(--border-subtle)', overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%',
                      background: pctFill, borderRadius: 99,
                      transition: 'width .15s',
                    }} />
                  </div>
                  <span className="mono" style={{
                    fontSize: 13.5, fontWeight: 500, color: pctReadout,
                    letterSpacing: '-0.02em',
                    minWidth: 44, textAlign: 'right',
                  }}>{pct.toFixed(1)}%</span>
                </div>
                {/* 6 — Calc Billed */}
                <div className="mono" style={{
                  padding: '0 12px', textAlign: 'right',
                  fontSize: 14.5, fontWeight: 500,
                  color: billed === 0 ? 'var(--text-4)' : 'var(--text-1)',
                  letterSpacing: '-0.02em',
                }}>{$f(billed)}</div>
              </div>
            );
          })}

          {/* Sticky totals footer */}
          <div style={{
            display: 'grid', gridTemplateColumns: GRID,
            minHeight: 56, alignItems: 'center',
            position: 'sticky', bottom: 0, zIndex: 3,
            background: 'var(--bg-surface-2)',
            borderTop: '1px solid var(--border-default)',
          }}>
            {/* Col 1: TOTALS + line count */}
            <div style={{ padding: '0 12px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{
                fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>Totals</span>
              <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-4)', letterSpacing: '-0.02em' }}>
                · {lines.length} {lines.length === 1 ? 'line' : 'lines'}
              </span>
            </div>
            {/* Col 2: total contract */}
            <div className="mono" style={{
              padding: '0 12px', textAlign: 'right',
              fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)',
              letterSpacing: '-0.02em',
            }}>{$f(totalContract)}</div>
            {/* Col 3: tasks_linked sum */}
            <div className="mono" style={{
              padding: '0 12px', textAlign: 'center',
              fontSize: 14.5, fontWeight: 600,
              color: totalTasksLinked === 0 ? 'var(--text-4)' : 'var(--text-1)',
              letterSpacing: '-0.02em',
            }}>{totalTasksLinked}</div>
            {/* Col 4: total allocated + % */}
            <div style={{ padding: '0 12px', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <div className="mono" style={{
                fontSize: 14.5, fontWeight: 600,
                color: totalAllocated === 0 ? 'var(--text-4)' : tile3Kind === 'success' ? 'var(--success)' : 'var(--warning)',
                letterSpacing: '-0.02em',
              }}>{$f(totalAllocated)}</div>
              <div className="mono" style={{
                fontSize: 12, opacity: 0.85,
                color: totalAllocated === 0 ? 'var(--text-4)' : tile3Kind === 'success' ? 'var(--success)' : 'var(--warning)',
                letterSpacing: '-0.02em',
              }}>{allocPctOfSov.toFixed(1)}%</div>
            </div>
            {/* Col 5: weighted-average % complete (same progress bar) */}
            <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                flex: 1, height: 6, borderRadius: 99,
                background: 'var(--border-subtle)', overflow: 'hidden',
              }}>
                <div style={{
                  width: `${Math.min(100, Math.max(0, weightedPct))}%`, height: '100%',
                  background: weightedPct >= 99.9 ? 'var(--success)' : weightedPct > 0 ? 'var(--accent)' : 'transparent',
                  borderRadius: 99,
                }} />
              </div>
              <span className="mono" style={{
                fontSize: 13.5, fontWeight: 600,
                color: weightedPct === 0 ? 'var(--text-4)' : weightedPct >= 99.9 ? 'var(--success)' : 'var(--text-1)',
                letterSpacing: '-0.02em',
                minWidth: 44, textAlign: 'right',
              }}>{weightedPct.toFixed(1)}%</span>
            </div>
            {/* Col 6: total billed */}
            <div className="mono" style={{
              padding: '0 12px', textAlign: 'right',
              fontSize: 14.5, fontWeight: 600,
              color: totalBilled === 0 ? 'var(--text-4)' : 'var(--text-1)',
              letterSpacing: '-0.02em',
            }}>{$f(totalBilled)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── Header cell ─────────────────────── */

function Th({ children, align = 'left', noRight = false }) {
  return (
    <div style={{
      padding: '0 12px',
      display: 'flex', alignItems: 'center',
      justifyContent: align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start'),
      fontSize: 12, fontWeight: 600, color: 'var(--text-3)',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      borderRight: noRight ? 'none' : '1px solid var(--border-subtle)',
      height: '100%',
      whiteSpace: 'nowrap',
    }}>{children}</div>
  );
}

export default RollupTab;
